import type { LiHTMLAttributes, ReactNode } from 'react'
import { AUFRISSSTUFE, einzug, rangklasse } from './Bericht'
import type { Gliederungsknoten } from './typen'

/**
 * Die Zeile eines Gliederungsbaums — **gezeichnet wie der Bericht** (7.2
 * Abschnitt 55, R1855–R1857).
 *
 * **Der Auftrag vom 2026-10-16:** *„Kannst du den Baum … näher an der
 * tatsächlichen Layoutstruktur der Ziel-Berichte angleichen, damit das
 * einheitlich wirkt?“* Dieselbe Gliederung stand an vier Stellen als Baum —
 * Importfenster, Kontofeld, *Positionspläne*, *Positionen wählen* —, und jede
 * zeichnete ihre Zeilen selbst, keine wie der Bericht.
 *
 * **Hier steht das Zeichnen einmal:** Rang (R570) aus `rangklasse`,
 * Einrückung aus `einzug` — dieselben beiden Stellen, die der Bericht benutzt —,
 * der Pfeil, und beim Konto das „·“ eine Aufrissstufe tiefer (R1856). Was eine
 * Stelle darüber hinaus kann — wählen, ziehen, anlegen —, bringt sie als
 * Inhalt und Ereignisse mit; die Zeile reicht beides an ihr `li` durch.
 *
 * `Gliederungsbaum.test.tsx` meldet jeden Baum der Gliederung, der seine Zeilen
 * wieder selbst zeichnet.
 */

/** Woraus der Rang einer Position folgt (R570) — ein Ast, den der Bericht nicht kennt, hat keinen. */
export type Rangquelle = Pick<Gliederungsknoten, 'istSumme' | 'istErgebnis' | 'rang'>

/** Der Pfeil einer Position. */
export type Pfeil = {
  offen: boolean
  /** Ohne etwas darunter steht der Platz leer, damit die Namen untereinander bleiben. */
  hatKinder: boolean
  /** Der vorgelesene Name des Knopfs. */
  beschriftung?: string
  aufKlappen?: () => void
  /**
   * Ein Zeichen statt eines Knopfs — wo die Zeile selbst das Element des
   * Baums ist und die Tastatur dort klappt (Kontofeld, R1043).
   */
  alsZeichen?: boolean
}

export function Baumzeile({
  art,
  ebene,
  rang,
  pfeil,
  blass = false,
  klasse,
  children,
  ...li
}: {
  art: 'position' | 'konto'
  /** Die Ebene der Position — ein Konto nennt die seiner Position (R1856). */
  ebene: number
  rang?: Rangquelle
  pfeil?: Pfeil
  /** Nichts darunter ist hier wählbar (R1612). */
  blass?: boolean
  /** Was die Stelle der Zeile zusätzlich anheftet, etwa die gewählte. */
  klasse?: string
  children: ReactNode
} & Omit<LiHTMLAttributes<HTMLLIElement>, 'className' | 'style' | 'children'>) {
  const klassen = [
    'berichtszeile',
    art === 'konto' ? 'istkonto' : '',
    art === 'position' && rang ? rangklasse(rang) : '',
    blass ? 'blass' : '',
    klasse ?? '',
  ]
    .filter((k) => k)
    .join(' ')

  return (
    <li
      className={klassen}
      style={{ paddingLeft: art === 'konto' ? einzug(ebene, AUFRISSSTUFE) : einzug(ebene) }}
      {...li}
    >
      {art === 'position' ? <Klapppfeil pfeil={pfeil} /> : <span className="baumpunkt" aria-hidden="true">·</span>}
      {children}
    </li>
  )
}

function Klapppfeil({ pfeil }: { pfeil?: Pfeil }) {
  const zeichen = pfeil?.hatKinder ? (pfeil.offen ? '▾' : '▸') : ''

  if (!pfeil || pfeil.alsZeichen) {
    const klappe = pfeil?.hatKinder ? pfeil.aufKlappen : undefined
    return (
      <span
        className="klapper"
        aria-hidden="true"
        onClick={
          klappe
            ? (ev) => {
                ev.stopPropagation()
                klappe()
              }
            : undefined
        }
      >
        {zeichen}
      </span>
    )
  }

  return (
    <button
      type="button"
      className="klapper"
      disabled={!pfeil.hatKinder}
      aria-expanded={pfeil.hatKinder ? pfeil.offen : undefined}
      aria-label={pfeil.beschriftung}
      onClick={pfeil.aufKlappen}
    >
      {zeichen}
    </button>
  )
}

/**
 * Was zu Beginn offen ist — **wie im Bericht** (R1857): jede Position, unter
 * der eine andere hängt. Eine Position, unter der nur Konten hängen, bleibt
 * zu; ihre Konten erscheinen auf Klick.
 *
 * **Das Bild kommt flach und mit Ebene** — eine Position hat Unterpositionen,
 * wenn die nächste Zeile tiefer steht.
 */
export function berichtsstand(knoten: readonly Pick<Gliederungsknoten, 'schluessel' | 'ebene'>[]): Set<string> {
  const offen = new Set<string>()
  knoten.forEach((k, i) => {
    const naechste = knoten[i + 1]
    if (naechste && naechste.ebene > k.ebene) {
      offen.add(k.schluessel)
    }
  })
  return offen
}
